import React, { createContext, FC, useContext, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUrlWithQueryParams } from 'src/shared/helpers';
import { useUnsavedChanges, UseUnsavedChangesReturn } from 'src/shared/hooks/useUnsavedChanges';
import { productConfigurationReducer, ProductConfigurationState, ProductConfigurationStateDispatch, ProductConfigurationStateReducer } from '.';
import pages from './pages';
import { initialState } from './stateInitialData';

interface ProductConfigurationContextValue {
    state: ProductConfigurationState;
    dispatch: ProductConfigurationStateDispatch;
    unsavedChanges: UseUnsavedChangesReturn<unknown>;
}

const noopUnsavedChanges: UseUnsavedChangesReturn<unknown> = {
    isDirty: false,
    resetSnapshot: () => {},
    saveRef: { current: null },
    guardNavigation: () => false,
    guardAction: () => false,
    NavigationGuard: null
};

const context = createContext<ProductConfigurationContextValue>({
    state: initialState,
    dispatch: () => new Error('Component not wrapped in product configuration state provider.'),
    unsavedChanges: noopUnsavedChanges
});

export const ProductConfigurationStateProvider: FC<{ children?: React.ReactNode; initialMode: 'none' | 'classic' | 'pod' }> = ({ children, initialMode }) => {
    const [state, dispatch] = useReducer<ProductConfigurationStateReducer>(productConfigurationReducer, { ...initialState, mode: initialMode });

    // Build product snapshot for change detection
    const productSnapshot = {
        name: state.product.name,
        code: state.product.code,
        providerCode: state.product.providerCode,
        imageUrl: state.product.imageUrl,
        colors: state.product.colors.map((color) => ({
            name: color.name,
            code: color.code,
            colorProviderCode: color.colorProviderCode,
            position: color.position,
            sides: color.sides.map((side) => ({
                imageUrl: side.imageUrl,
                ppcm: side.ppcm,
                code: side.code,
                sortOrder: side.sortOrder,
                mandatory: side.mandatory,
                canRotate: side.canRotate,
                originalSideGuid: side.originalSideGuid,
                areas: side.areas.map((area) => ({
                    name: area.name,
                    pathSVG: area.pathSVG,
                    boundsX: area.boundsX,
                    boundsY: area.boundsY,
                    boundsWidth: area.boundsWidth,
                    boundsHeight: area.boundsHeight,
                    clipOut: area.clipOut
                }))
            }))
        })),
        sidesCount: state.sides.length,
        sideNames: state.sides.map((s) => s.name)
    };

    const unsavedChanges = useUnsavedChanges(productSnapshot);

    return <context.Provider value={{ state, dispatch, unsavedChanges }}> {children} </context.Provider>;
};

export const useProductConfigurationState = () => {
    return useContext(context);
};

export const useProductConfigurationChangeStep = () => {
    const navigate = useNavigate();

    return (step: (typeof pages)[number]['key'], keepParams: boolean = true) => {
        const page = pages.find((x) => x.key == step);
        if (page) navigate(keepParams ? getUrlWithQueryParams(page.url) : page.url);
    };
};
